import { useMemo, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "untitledui-js/react";
import { PonLogo } from "@components/PonLogo";
import { VacancyCard } from "@components/VacancyCard";
import { useConfig } from "@hooks/useConfig";
import { useGetVacancies } from "@hooks/useGetVacancies";
import { MenuLink } from "../types/NavigationTypes";
import { useStoryblokTranslatedSlugs } from "@hooks/useStoryblokTranslatedSlugs";
import { languageDutch } from "../i18n";
import { Typography, Button } from "@pondigitalsolutions/pon-component-library";
import { buildLocalizedHrefForMenuLink } from "@utils/slugUtils";
import { useCurrentLang } from "@hooks/useCurrentLang";

export const Footer = () => {
  const { t } = useTranslation();
  const { data } = useConfig();
  const currentLang = useCurrentLang();

  // State to track which vacancy cards should be non-tabbable
  const [hiddenCardIndexes, setHiddenCardIndexes] = useState<Set<number>>(
    new Set()
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch recent vacancies for the footer
  const { data: vacancies } = useGetVacancies({
    per_page: 14,
    locale: currentLang,
  });

  // Get the external and footer menu links from the config in the CMS
  const {
    externalMenuLinks,
    footerMenuLinks,
  }: { externalMenuLinks: MenuLink[]; footerMenuLinks: MenuLink[] } =
    useMemo(() => {
      // Check if the data is available and if the content is present
      if (!data?.storyData?.content) {
        return { externalMenuLinks: [], footerMenuLinks: [] };
      }

      const externalMenu =
        currentLang === languageDutch
          ? (data.storyData.content["externalMenu"] as MenuLink[] | undefined)
          : (data.storyData.content["externalMenuEN"] as
              | MenuLink[]
              | undefined);
      const footerMenu =
        currentLang === languageDutch
          ? (data.storyData.content["footerMenu"] as MenuLink[] | undefined)
          : (data.storyData.content["footerMenuEN"] as MenuLink[] | undefined);
      return {
        externalMenuLinks: externalMenu ?? [],
        footerMenuLinks: footerMenu ?? [],
      };
    }, [data, currentLang]);

  // Build a list of UUIDs for internal links so we can fetch localized slugs when not embedded
  const internalLinkUuids = useMemo(() => {
    // Only fetch localized slugs for footerMenuLinks when on non-Dutch routes
    if (currentLang === languageDutch) return [] as string[];
    return footerMenuLinks
      .map(({ link }) => {
        if (!link || link.linktype !== "story") return undefined;
        const hasLangTranslated = link.story?.translated_slugs?.some(
          (s) => s.lang === currentLang
        );
        return hasLangTranslated ? undefined : link.id;
      })
      .filter((id): id is string => Boolean(id));
  }, [footerMenuLinks, currentLang]);

  const { map: localizedSlugByUuid } = useStoryblokTranslatedSlugs(
    internalLinkUuids,
    currentLang
  );

  // Effect to detect which vacancy cards are hidden and make them non-tabbable
  useEffect(() => {
    if (!containerRef.current || !vacancies?.data?.length) return;

    const calculateHiddenCards = () => {
      const container = containerRef.current;
      if (!container) return;

      // Only target desktop cards for tabIndex calculation
      const desktopContainer = container.querySelector(".hidden.md\\:contents");
      if (!desktopContainer) return;

      const cards = desktopContainer.querySelectorAll("[data-vacancy-card]");
      if (cards.length === 0) return;

      const containerRect = container.getBoundingClientRect();
      const containerBottom = containerRect.bottom;
      const newHiddenIndexes = new Set<number>();

      cards.forEach((card, index) => {
        const cardElement = card as HTMLElement;
        const cardRect = cardElement.getBoundingClientRect();
        const cardTop = cardRect.top;

        // Only consider a card hidden if it starts below the container's bottom
        if (cardTop >= containerBottom) {
          newHiddenIndexes.add(index);
        }
      });

      setHiddenCardIndexes(newHiddenIndexes);
    };

    const detectHiddenCards = () => {
      // Wait for the next frame to ensure all DOM updates are complete
      requestAnimationFrame(calculateHiddenCards);
    };

    // Initial detection with a delay to ensure layout is complete after data load
    const timeoutId = setTimeout(detectHiddenCards, 200);

    // Set up ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(detectHiddenCards);
    resizeObserver.observe(containerRef.current);

    // Set up window resize listener for broader resize detection
    const handleWindowResize = () => {
      setTimeout(detectHiddenCards, 100);
    };
    window.addEventListener("resize", handleWindowResize);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [vacancies?.data]);

  return (
    <div className="bg-tertiary-50 dark:bg-secondary-900">
      <div className="mx-auto flex max-w-360 flex-wrap items-center px-4 max-md:flex-col max-md:gap-6 2xl:px-0">
        {/* Recent Vacancies Section */}
        <div className="flex flex-row items-end pt-9 md:pt-14 md:pb-7">
          <div className="flex flex-1 flex-col items-start justify-start">
            {/* Header */}
            <div className="mb-4 flex flex-row items-center justify-center gap-2 max-md:w-full md:justify-start">
              <Typography
                as="h3"
                className="text-lg md:text-xl dark:text-white"
              >
                {t("Footer.recentVacancies")}
              </Typography>

              <Button
                id="recent-vacancies-button"
                variant="secondary"
                size="sm"
                className="pointer-events-auto hidden w-fit gap-1 md:flex"
                endIcon="ArrowUpRight"
                renderProp={
                  <Link
                    to={
                      currentLang === languageDutch
                        ? "/vacancies"
                        : `/${currentLang}/vacancies`
                    }
                  />
                }
              >
                {t("Footer.viewAllVacancies")}
              </Button>
            </div>

            <div className="md:flex">
              {/* Vacancy Cards Grid */}
              {/* For larger screens we added an max-height so we have a maximum of 2 columns.
                  We use padding (p-1) to provide space for focus outlines and negative margin (-m-1) 
                  to maintain the original visual positioning without affecting the layout. */}
              <div
                ref={containerRef}
                className="custom-scrollbar flex w-full items-end justify-start gap-3 max-md:max-w-[calc((100vw)-(--spacing(16)))] max-md:overflow-x-scroll md:-m-1 md:max-h-32 md:flex-wrap md:overflow-hidden md:p-1"
              >
                {/* Mobile: Show first 8 vacancies */}
                <div className="contents md:hidden">
                  {vacancies?.data
                    ?.slice(0, 8)
                    ?.map((vacancy) => (
                      <VacancyCard
                        key={vacancy.id}
                        vacancy={vacancy}
                        data-vacancy-card
                      />
                    )) ?? []}
                </div>
                {/* Desktop: Show all vacancies, but make hidden ones non-tabbable */}
                <div className="hidden md:contents">
                  {vacancies?.data?.map((vacancy, index) => (
                    <VacancyCard
                      key={vacancy.id}
                      vacancy={vacancy}
                      tabIndex={hiddenCardIndexes.has(index) ? -1 : undefined}
                      data-vacancy-card
                    />
                  )) ?? []}
                </div>
              </div>

              {/* Navigation Menu */}
              <div
                className="flex h-full flex-col justify-end gap-2 max-md:pt-6 md:ml-12 md:items-start"
                aria-label={t("Footer.navExternalAriaLabel")}
              >
                {externalMenuLinks.map((item: MenuLink) => (
                  <Link
                    key={item._uid}
                    to={item.link?.cached_url ? `${item.link.cached_url}` : "#"}
                    className="flex w-full flex-row items-center justify-center gap-1 hover:underline md:justify-start"
                    target={
                      item.link && item.link.linktype === "url"
                        ? "_blank"
                        : "_self"
                    }
                  >
                    <Typography className="text-nowrap dark:text-white">
                      {item.Label}
                    </Typography>
                    <div className="flex items-center justify-center">
                      <ArrowUpRight
                        strokeWidth={1.5}
                        className="stroke-tertiary-500"
                      />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section with logo and legal links */}
        <div className="mx-auto flex w-full flex-col items-center justify-between max-md:gap-6 md:my-8 md:flex-row">
          <Link
            to={currentLang === languageDutch ? "/" : `/${currentLang}`}
            aria-label={t("Global.homeLogoAriaLabel")}
          >
            <PonLogo className="text-secondary-700 dark:text-white" />
          </Link>

          <div
            className="flex flex-row items-center justify-start gap-4 max-md:mb-9"
            aria-label={t("Footer.navLegalAriaLabel")}
          >
            {footerMenuLinks.length > 0 && (
              <>
                {footerMenuLinks.map((item: MenuLink) => {
                  const href = buildLocalizedHrefForMenuLink(
                    item,
                    currentLang,
                    localizedSlugByUuid
                  );
                  return (
                    <Link
                      key={item._uid}
                      to={href}
                      className="text-xs font-semibold text-tertiary-500 *:hover:underline *:hover:underline-offset-2 dark:text-white/70"
                    >
                      {item.Label}
                    </Link>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
